import { ReactElement, useCallback } from 'react';

import { PatientConsentDocumentQuery } from '@dreryk/egabinet-patient-frontend-api';

import { refetchCqrsQuery } from '@dreryk/command-query-react-common';
import { createTranslatedText } from '@dreryk/react-common';
import { Modal, ModalCancelButton, ModalCloseButton, ModalMaxHeight, ModalWidth } from '@dreryk/react-components';

import { DocumentTemplateForm } from '~libs.desktop.shared.document-template';

import { ConsentsAndAuthorizationsDocumentTemplateLoader } from './ConsentsAndAuthorizationsDocumentTemplate.loader';
import { ConsentsAndAuthorizationsDocumentTemplateApproveAndPrintButton } from './ConsentsAndAuthorizationsDocumentTemplateApproveAndPrintButton';
import { StyledConsentsAndAuthorizationsDocumentTemplateModal } from './ConsentsAndAuthorizationsDocumentTemplateModal.styled';
import { modalConfirmingExitConfig } from './ConsentsAndAuthorizationsDocumentTemplateModal.utils';

type ConsentsAndAuthorizationsDocumentTemplateModalProps = {
   readonly onApproveAndPrint: VoidFunction;
   readonly onClose: VoidFunction;
   readonly patientId: number;
   readonly printTemplateName: string;
   readonly printTemplateId: number;
};

export const ConsentsAndAuthorizationsDocumentTemplateModal = (
   props: ConsentsAndAuthorizationsDocumentTemplateModalProps
): ReactElement => {
   const handleApproveAndPrintSuccess = useCallback(() => {
      refetchCqrsQuery(PatientConsentDocumentQuery);
      props.onApproveAndPrint();
      props.onClose();

      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.onClose, props.onApproveAndPrint]);

   return (
      <Modal
         title={createTranslatedText(props.printTemplateName)}
         width={ModalWidth.HUGE}
         height={ModalMaxHeight.TALL}
         testId={'consentsAndAuthorizationsDocumentTemplate'}
      >
         <StyledConsentsAndAuthorizationsDocumentTemplateModal>
            <ConsentsAndAuthorizationsDocumentTemplateLoader
               patientId={props.patientId}
               printTemplateId={props.printTemplateId}
            >
               {({ patientConsentDocumentTemplate }) => (
                  <DocumentTemplateForm documentTemplate={patientConsentDocumentTemplate}>
                     {({ handleSubmit, formState }) => (
                        <>
                           <ConsentsAndAuthorizationsDocumentTemplateApproveAndPrintButton
                              handleSubmit={handleSubmit}
                              onSuccess={handleApproveAndPrintSuccess}
                              patientId={props.patientId}
                              printTemplateId={props.printTemplateId}
                           />

                           <ModalCloseButton
                              onClick={props.onClose}
                              shouldConfirmExit={modalConfirmingExitConfig(formState.isDirty)}
                           />
                           <ModalCancelButton
                              onClick={props.onClose}
                              shouldConfirmExit={modalConfirmingExitConfig(formState.isDirty)}
                           />
                        </>
                     )}
                  </DocumentTemplateForm>
               )}
            </ConsentsAndAuthorizationsDocumentTemplateLoader>
         </StyledConsentsAndAuthorizationsDocumentTemplateModal>
      </Modal>
   );
};
